import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { calculateModuleTimeSaved, formatTimeSaved } from '../services/analyticsService';

interface Module {
  id: string;
  title: string;
  description: string;
  module_type: string;
  tags: string[];
  created_at: string;
  created_by: string;
}

type IdeaLane = 'problem' | 'solution' | null;

const GrowPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedLane, setSelectedLane] = useState<IdeaLane>(null);
  const [ideaTitle, setIdeaTitle] = useState('');
  const [ideaDescription, setIdeaDescription] = useState('');
  const [ideaTeam, setIdeaTeam] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [latestModules, setLatestModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: modules } = await supabase
        .from('modules')
        .select('id, title, description, module_type, tags, created_at, created_by')
        .order('created_at', { ascending: false })
        .limit(6);

      setLatestModules(modules || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIdea = async () => {
    if (!ideaTitle.trim() || !ideaDescription.trim() || !selectedLane || !user) return;

    setSubmitting(true);
    try {
      const userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email || '';

      const { error } = await supabase
        .from('ideas')
        .insert({
          title: ideaTitle.trim(),
          description: ideaDescription.trim(),
          idea_type: selectedLane,
          submitter_id: user.id,
          submitter_name: userName,
          team: ideaTeam.trim() || null,
          status: 'new'
        });

      if (error) {
        // Fallback: save to workflow_insights if ideas table doesn't exist yet
        if (error.code === '42P01') {
          await supabase.from('workflow_insights').insert({
            user_id: user.id,
            description: `[${selectedLane}] ${ideaTitle}: ${ideaDescription}`,
            created_at: new Date().toISOString()
          });
        } else {
          console.error('Error submitting idea:', error);
        }
      }

      setSubmitted(true);
      setIdeaTitle('');
      setIdeaDescription('');
      setIdeaTeam('');
      setSelectedLane(null);
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting idea:', error);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-10">
        {/* Hero Section */}
        <div className="text-center py-6 sm:py-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-teal-600 dark:bg-teal-500 mb-4">
            <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8m0 0c-4 0-7-3-7-8 4 0 7 3 7 8zm0 0c4 0 7-3 7-8-4 0-7 3-7 8z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
            Welcome to the Garden
          </h1>
          <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto px-4 sm:px-0">
            Discover AI tools, automations, and workflows that will help you save hours every week.
          </p>
        </div>

        {/* Seed an Idea Section */}
        <div className="bg-teal-50 dark:bg-teal-500/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-teal-200 dark:border-teal-500/20">
          <div className="flex items-start gap-3 sm:gap-4 mb-5">
            <div className="p-2 bg-teal-100 dark:bg-teal-500/20 rounded-lg flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
              </svg>
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100">Seed an Idea</h2>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                Share a problem you've spotted or a solution you've built. Your ideas fuel company-wide innovation.
              </p>
            </div>
          </div>

          {/* Two-Lane Selection */}
          {!selectedLane && !submitted && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <button
                onClick={() => setSelectedLane('problem')}
                className="p-4 sm:p-5 bg-white dark:bg-zinc-900 rounded-xl border-2 border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all text-left group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">Share a Problem / Opportunity</span>
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Something taking too much time, repetitive workflows, inefficiencies, or AI opportunities
                </p>
              </button>

              <button
                onClick={() => setSelectedLane('solution')}
                className="p-4 sm:p-5 bg-white dark:bg-zinc-900 rounded-xl border-2 border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all text-left group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                  </svg>
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">Share a Built Idea / Solution</span>
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  GPTs, Claude artifacts, workflows, automations, prompt systems, or internal tools you've built
                </p>
              </button>
            </div>
          )}

          {/* Submission Form */}
          {selectedLane && !submitted && (
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                  selectedLane === 'problem'
                    ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                    : 'bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400'
                }`}>
                  {selectedLane === 'problem' ? 'Problem / Opportunity' : 'Built Idea / Solution'}
                </span>
                <button
                  onClick={() => setSelectedLane(null)}
                  className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                >
                  Change
                </button>
              </div>

              <input
                type="text"
                value={ideaTitle}
                onChange={(e) => setIdeaTitle(e.target.value)}
                placeholder={selectedLane === 'problem' ? 'Give your problem/opportunity a title...' : 'Name your solution or tool...'}
                className="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
              />

              <textarea
                value={ideaDescription}
                onChange={(e) => setIdeaDescription(e.target.value)}
                placeholder={selectedLane === 'problem'
                  ? 'Describe the problem: What takes too long? What could be automated? What would save your team hours?'
                  : 'Describe your solution: What does it do? How does it help? Could other teams benefit from it?'
                }
                className="w-full h-24 sm:h-28 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 resize-none"
              />

              <input
                type="text"
                value={ideaTeam}
                onChange={(e) => setIdeaTeam(e.target.value)}
                placeholder="Your team or department (optional)"
                className="w-full px-3 sm:px-4 py-2.5 text-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
              />

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
                <p className="text-xs text-zinc-500 text-center sm:text-left">
                  Your idea will appear in the Solutions Feed for the team to engage with
                </p>
                <button
                  onClick={handleSubmitIdea}
                  disabled={!ideaTitle.trim() || !ideaDescription.trim() || submitting}
                  className="px-5 py-2.5 sm:py-2 rounded-lg text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {submitting ? 'Submitting...' : 'Submit Idea'}
                </button>
              </div>
            </div>
          )}

          {/* Success State */}
          {submitted && (
            <div className="text-center py-4 animate-fade-in">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-500/20 mb-3">
                <svg className="w-6 h-6 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Idea submitted!</p>
              <p className="text-xs text-zinc-500 mt-1">Check the Solutions Feed to see engagement on your idea.</p>
            </div>
          )}
        </div>

        {/* Navigation Cards - Links to key sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Link
            to="/solutions"
            className="p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-7 h-7 text-teal-600 dark:text-teal-400 mb-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
            </svg>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              Solutions Feed
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Vote on ideas and track progress
            </p>
          </Link>

          <Link
            to="/roadmap"
            className="p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-7 h-7 text-teal-600 dark:text-teal-400 mb-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
            </svg>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              Roadmap
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              See what Innovation is building
            </p>
          </Link>

          <Link
            to="/modules"
            className="p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-7 h-7 text-teal-600 dark:text-teal-400 mb-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              Resources
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Modules, guides, and AI highlights
            </p>
          </Link>

          <Link
            to="/ai-highlights"
            className="p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-7 h-7 text-teal-600 dark:text-teal-400 mb-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              AI Highlights
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Real AI wins across teams
            </p>
          </Link>
        </div>

        {/* Latest in the Garden */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100">Latest in the Garden</h2>
            <Link to="/modules" className="text-xs sm:text-sm text-teal-600 dark:text-teal-400 hover:underline">
              View all →
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white dark:bg-zinc-800 rounded-lg p-4 sm:p-5 border border-zinc-200 dark:border-zinc-700 animate-pulse">
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4 mb-3"></div>
                  <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-full mb-2"></div>
                  <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {latestModules.map((module, index) => (
                <Link
                  key={module.id}
                  to={`/modules/${module.id}`}
                  className="bg-white dark:bg-zinc-800 rounded-lg p-4 sm:p-5 border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all hover-lift group animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {module.module_type && (
                      <span className="px-2 py-0.5 text-xs bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400 rounded">
                        {module.module_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-xs bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400 rounded">
                      ~{formatTimeSaved(calculateModuleTimeSaved(module.module_type || '', module.tags || []))}/wk
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors line-clamp-1">
                    {module.title}
                  </h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-500 mt-1 line-clamp-2">
                    {module.description}
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    {new Date(module.created_at).toLocaleDateString()}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GrowPage;
